import MuxPlayer from '@mux/mux-player-react'
import { muxConfig } from '../../config/mux.config'
import type { Video } from '../../types/database.types'

interface VideoPlayerProps {
  video: Video
  autoPlay?: boolean
  onProgress?: (currentTime: number, duration: number) => void
  onEnded?: () => void
  className?: string
}

export function VideoPlayer({
  video,
  autoPlay = false,
  onProgress,
  onEnded,
  className = '',
}: VideoPlayerProps) {
  if (!video.mux_playback_id) {
    return (
      <div
        className={`aspect-video bg-gray-900 rounded-xl flex items-center justify-center ${className}`}
      >
        <div className="text-center text-gray-400">
          <svg
            className="mx-auto h-12 w-12 mb-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p>Video not available</p>
          {video.status === 'processing' && (
            <p className="text-sm mt-1">Video is still processing...</p>
          )}
          {video.status === 'error' && (
            <p className="text-sm mt-1 text-red-400">
              {video.error_message || 'An error occurred'}
            </p>
          )}
        </div>
      </div>
    )
  }

  const handleTimeUpdate = (e: Event) => {
    if (!onProgress) return
    const player = e.target as HTMLVideoElement
    onProgress(player.currentTime, player.duration)
  }

  return (
    <div className={`aspect-video rounded-xl overflow-hidden bg-black relative ${className}`}>
      <MuxPlayer
        playbackId={video.mux_playback_id}
        metadata={{
          video_id: video.id,
          video_title: video.title,
        }}
        streamType="on-demand"
        autoPlay={autoPlay}
        muted={autoPlay}
        accentColor={muxConfig.playback.accentColor}
        primaryColor={muxConfig.playback.primaryColor}
        onTimeUpdate={handleTimeUpdate}
        onEnded={onEnded}
        style={{ height: '100%', width: '100%' }}
      />
      {/* Watermark Overlay */}
      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 pointer-events-none select-none">
        <div className="flex items-center gap-2 bg-black/30 backdrop-blur-sm px-3 py-1.5 rounded-lg">
          <img
            src="/logo-icon.png"
            alt=""
            className="w-5 h-5 object-contain opacity-70"
          />
          <span className="text-white/70 text-sm font-semibold tracking-wide">
            ExerciseToolbox
          </span>
        </div>
      </div>
    </div>
  )
}

// Simple thumbnail component for when you just need the image
interface VideoThumbnailProps {
  video?: Video
  playbackId?: string | null
  alt?: string
  className?: string
  width?: number
  height?: number
}

export function VideoThumbnail({
  video,
  playbackId,
  alt,
  className = '',
  width = 640,
  height = 360,
}: VideoThumbnailProps) {
  const id = playbackId ?? video?.mux_playback_id
  const altText = alt ?? video?.title ?? 'Video thumbnail'

  if (!id) {
    return (
      <div
        className={`bg-gray-200 flex items-center justify-center rounded-lg ${className}`}
        style={{ aspectRatio: `${width}/${height}` }}
      >
        <svg
          className="h-8 w-8 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      </div>
    )
  }

  // Calculate thumbnail time: use saved value, fallback to 25% of duration, then 0
  const thumbnailTime = video?.thumbnail_time
    ?? (video?.duration_seconds ? video.duration_seconds * 0.25 : 0)

  const thumbnailUrl = muxConfig.getThumbnailUrl(id, { width, height, time: thumbnailTime })

  return (
    <img
      src={thumbnailUrl}
      alt={altText}
      className={`object-cover rounded-lg ${className}`}
      loading="lazy"
    />
  )
}
